// src/pages/seasons/RegisterSuccess.jsx
// Landing page after successful Stripe payment.
// Mirrors the SuccessScreen sub-component in RegisterPage so a parent
// arriving here from Stripe Checkout sees the same lime/pitch identity
// they saw during the registration flow.
'use client';

import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { CheckIcon, DocumentArrowUpIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import InvoiceDownload from '@/components/InvoiceDownload';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

// Bilingual copy keyed off the language the parent chose during
// registration (stored on the registration doc) — the page renders in
// English for the moment before the doc loads, then flips if needed.
const STRINGS = {
  en: {
    eyebrow: 'Payment confirmed',
    title: "You're in!",
    subA: 'Registration for',
    subWith: (t) => ` with ${t}`,
    subB: ' is complete.',
    thisSeason: 'this season',
    checklist: [
      'Payment received & confirmed',
      'Registration complete & saved',
      'Waiver signed & on file',
      'Receipt sent to your email',
    ],
    docsTitle: 'One more step: upload required documents',
    docsOne: "Your coach needs a copy of your player's eligibility document to confirm them on the roster.",
    docsMany: (n) => `Your coach needs ${n} eligibility documents to confirm your player on the roster.`,
    uploadCta: 'Upload documents now',
    docsFallback: 'Your coach may reach out about eligibility documents (birth certificate, player photo).',
    recordsTitle: 'Need a copy for your records?',
    questions: (t) => `Questions? Contact ${t} directly.`,
    questionsFallback: 'Questions? Contact your coach directly.',
  },
  es: {
    eyebrow: 'Pago confirmado',
    title: '¡Ya está dentro!',
    subA: 'La inscripción para',
    subWith: (t) => ` con ${t}`,
    subB: ' está completa.',
    thisSeason: 'esta temporada',
    checklist: [
      'Pago recibido y confirmado',
      'Inscripción completa y guardada',
      'Exención firmada y archivada',
      'Recibo enviado a tu correo',
    ],
    docsTitle: 'Un paso más: sube los documentos requeridos',
    docsOne: 'Tu entrenador necesita una copia del documento de elegibilidad de tu jugador para confirmarlo en el equipo.',
    docsMany: (n) => `Tu entrenador necesita ${n} documentos de elegibilidad para confirmar a tu jugador en el equipo.`,
    uploadCta: 'Subir documentos ahora',
    docsFallback: 'Tu entrenador podría pedirte documentos de elegibilidad (acta de nacimiento, foto del jugador).',
    recordsTitle: '¿Necesitas una copia para tus registros?',
    questions: (t) => `¿Preguntas? Contacta directamente a ${t}.`,
    questionsFallback: '¿Preguntas? Contacta directamente a tu entrenador.',
  },
};

export default function RegisterSuccess() {
  const { teamId, seasonId } = useParams();
  const [searchParams] = useSearchParams();
  const registrationId = searchParams.get('rid');
  const [season, setSeason] = useState(null);
  const [team,   setTeam]   = useState(null);
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    const fetches = [
      getDoc(doc(db, 'seasons', seasonId)),
      getDoc(doc(db, 'teams',   teamId)),
    ];
    if (registrationId) {
      fetches.push(getDoc(doc(db, 'registrations', registrationId)));
    }
    Promise.all(fetches).then(([sSnap, tSnap, rSnap]) => {
      if (sSnap.exists()) setSeason({ id: sSnap.id, ...sSnap.data() });
      if (tSnap.exists()) setTeam({   id: tSnap.id, ...tSnap.data() });
      if (rSnap?.exists()) setRegistration({ id: rSnap.id, ...rSnap.data() });
    });
  }, [seasonId, teamId, registrationId]);

  const lang = String(registration?.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
  const s = STRINGS[lang];

  return (
    <div className="min-h-screen bg-[#f7faf5] flex items-center justify-center p-6">
      <div className="max-w-md w-full">

        {/* Hero — celebratory eyebrow + headline mirrors the in-page
            SuccessScreen in RegisterPage.jsx so the parent's confirmation
            visual is consistent whether they arrive via Stripe redirect
            (here) or through the in-page success state. */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🎉</div>
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {s.eyebrow}
          </p>
          <h1
            className="text-3xl font-bold uppercase tracking-tight text-[#0d1f14] mt-1"
            style={{ fontFamily: HEAD_FONT }}
          >
            {s.title}
          </h1>
          <p className="text-zinc-600 mt-2 text-sm">
            {s.subA} <strong className="text-[#0d1f14]">{season?.name ?? s.thisSeason}</strong>
            {team?.name ? s.subWith(team.name) : ''}{s.subB}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
          {/* Team header — pitch-dark mini-band with lime-ringed logo,
              same treatment as the SuccessScreen in RegisterPage so the
              parent's brand impression carries through to the receipt. */}
          {(team?.logoUrl || team?.name) && (
            <div className="flex items-center gap-3 px-6 py-4 border-b border-zinc-100 bg-[#0d1f14]">
              {team?.logoUrl ? (
                <img src={team.logoUrl} alt={team.name}
                  className="h-10 w-10 rounded-xl object-cover ring-2 ring-[#b5f03d]/40 bg-white p-0.5 flex-shrink-0" />
              ) : (
                <div className="h-10 w-10 rounded-xl bg-[#b5f03d] flex items-center justify-center flex-shrink-0">
                  <span
                    className="text-sm font-bold text-[#0d1f14] uppercase"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {team?.name?.[0]}
                  </span>
                </div>
              )}
              <div>
                <p className="font-bold text-white text-sm" style={{ fontFamily: HEAD_FONT }}>{team?.name}</p>
                <p className="text-xs text-[#b5f03d]/90">{season?.name}</p>
              </div>
            </div>
          )}

          {/* Checklist — lime-themed checkmarks since this is the
              "everything went well" confirmation. */}
          <div className="px-6 py-5 space-y-3">
            {s.checklist.map((text, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="h-5 w-5 rounded-full bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckIcon className="h-3 w-3 text-[#5a8a1f] stroke-[3]" />
                </div>
                <p className="text-sm text-zinc-700">{text}</p>
              </div>
            ))}

            {/* Documents — two render modes:
                  (a) Required docs configured on the season → prominent
                      lime CTA linking straight to the public upload
                      page. The just-paid moment is the highest-attention
                      window we'll ever have for compliance, so we
                      surface the action here rather than relying on
                      the parent finding the link in the confirmation
                      email later (which they often don't).
                  (b) No required docs → smaller passive note, since
                      coaches occasionally collect docs ad-hoc and we
                      don't want to look pushy when nothing's required. */}
            {Array.isArray(season?.requiredDocTypes) && season.requiredDocTypes.length > 0 ? (
              <div className="mt-2 rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/10 p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="h-8 w-8 rounded-lg bg-[#b5f03d]/30 flex items-center justify-center flex-shrink-0">
                    <DocumentArrowUpIcon className="h-4 w-4 text-[#0d1f14]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[#0d1f14] leading-tight">
                      {s.docsTitle}
                    </p>
                    <p className="mt-1 text-xs text-zinc-600 leading-snug">
                      {season.requiredDocTypes.length === 1
                        ? s.docsOne
                        : s.docsMany(season.requiredDocTypes.length)}
                    </p>
                  </div>
                </div>
                {registrationId && (
                  <Link
                    to={`/upload/${teamId}/${registrationId}`}
                    className="inline-flex items-center gap-1.5 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3 py-2 text-xs font-bold uppercase transition-colors"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                  >
                    <DocumentArrowUpIcon className="h-3.5 w-3.5" />
                    {s.uploadCta}
                    <ArrowRightIcon className="h-3 w-3" />
                  </Link>
                )}
              </div>
            ) : (
              <div className="flex items-start gap-3 pt-1">
                <div className="h-5 w-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[10px]">📄</span>
                </div>
                <p className="text-sm text-zinc-600">
                  {s.docsFallback}
                </p>
              </div>
            )}
          </div>

          {registration && (
            <div className="px-6 pt-1 pb-5 border-t border-zinc-100">
              <p
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-zinc-500 mb-3 pt-3"
                style={{ fontFamily: HEAD_FONT }}
              >
                {s.recordsTitle}
              </p>
              <InvoiceDownload registration={registration} size="sm" />
            </div>
          )}

          <div className="px-6 pb-5">
            <p className="text-xs text-zinc-500 text-center">
              {team?.name ? s.questions(team.name) : s.questionsFallback}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
